package com.gastoncantera.intercom.push

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update

/**
 * Deriva título/subtítulo de un callId ("consorcio|torre|depto"), igual que buildLabel en
 * functions/src/notify.ts, para poder rearmar la etiqueta de la perdida sin depender del payload.
 */
internal fun missedCallLabel(callId: String): Pair<String, String> {
    val parts = callId.split("|")
    val title = parts.getOrNull(0)?.takeIf { it.isNotBlank() } ?: "Portero"
    val extras = listOfNotNull(
        parts.getOrNull(1)?.takeIf { it.isNotBlank() }?.let { "Torre $it" },
        parts.getOrNull(2)?.takeIf { it.isNotBlank() },
    )
    return title to extras.joinToString(" · ")
}

/**
 * Perdidas para plataformas donde la notificación la muestra el SO (iOS y Web): el cliente no corre
 * al mostrarse (a diferencia de Android), así que solo se entera al llegar el push "dismiss_call"
 * (el portero colgó / terminó la llamada). Cuenta perdida salvo que el usuario ya la haya manejado
 * (rechazó desde la notificación o tocó para abrir la app).
 *
 * En memoria: iOS re-hidrata por push en background; Web persiste aparte en IndexedDB (ver fcm.js).
 */
internal object MissedCallStore {
    private val missed = mutableListOf<MissedCall>()
    private val handled = mutableSetOf<String>()

    private val _tick = MutableStateFlow(0)
    val tick: StateFlow<Int> = _tick.asStateFlow()

    /** El usuario atendió/rechazó/abrió esta llamada: no contarla como perdida. */
    fun markHandled(callId: String) {
        handled += callId
    }

    /** Llegó dismiss_call. Registra la perdida salvo que el usuario ya la haya manejado. */
    fun onDismissed(callId: String, timeLabel: String) {
        if (handled.remove(callId)) return
        val (title, subtitle) = missedCallLabel(callId)
        missed += MissedCall(title, subtitle, timeLabel)
        _tick.update { it + 1 }
    }

    /** Alta directa (Web: entradas ya persistidas en IndexedDB por el service worker). */
    fun addMissed(call: MissedCall) {
        missed += call
        _tick.update { it + 1 }
    }

    fun replaceAll(calls: List<MissedCall>) {
        missed.clear()
        missed += calls
        _tick.update { it + 1 }
    }

    fun pending(): List<MissedCall> = missed.toList()

    fun clear() {
        missed.clear()
    }

    fun onForeground() {
        if (missed.isNotEmpty()) _tick.update { it + 1 }
    }
}
